/**
 * Headless selectors for the Insights → Campaign Impact tab.
 *
 * Reuses the Daily tab's day projection (`projectDailyRevenue`) for the
 * booked revenue line — one consistent revenue series shared across both
 * tabs — and adds only what's new here: projecting each campaign's send date
 * onto the same short-label x-axis the chart plots, so a `ReferenceLine`
 * marker lands on the correct day, and reporting the sends that have no such
 * day so the chart can disclose them instead of dropping them.
 */

import type {
  CampaignImpactMarker,
  InsightsDailyRevenuePoint,
} from "../data/api-types";
import {
  formatGbpCompact,
  niceAxisMax,
  projectDailyRevenue,
  shortDayLabel,
  totalRevenue,
  type DailyRevenueDatum,
} from "./insights-daily-revenue";

export { formatGbpCompact, niceAxisMax, projectDailyRevenue, totalRevenue };
export type { DailyRevenueDatum };

/** One campaign send projected onto the chart's day-label x-axis. */
export interface CampaignMarkerDatum {
  campaignId: string;
  campaignName: string;
  sentAt: string;
  /** Short axis label matching the day this marker lands on, e.g. "18 May". */
  label: string;
}

/** Campaign sends split by whether the chart has a day to anchor them to. */
export interface CampaignMarkerProjection {
  /** Sends landing on a plotted day — drawn as reference-line markers. */
  plotted: CampaignMarkerDatum[];
  /**
   * Sends the chart cannot mark, kept so the caller can say so rather than
   * undercount silently. `revenue_performance_daily` carries only days with
   * order intake or invoicing — it is not a dense calendar spine — so a
   * campaign sent on a Sunday, bank holiday, or shutdown day has no x-axis
   * tick to attach a marker to. A window boundary mismatch between the two
   * independently-windowed series lands here too.
   */
  unplotted: CampaignImpactMarker[];
}

/** Project each campaign send onto the chart's day labels. */
export function projectCampaignMarkers(
  markers: CampaignImpactMarker[],
  days: InsightsDailyRevenuePoint[],
): CampaignMarkerProjection {
  const plottedDays = new Set(days.map((d) => d.day));
  const plotted: CampaignMarkerDatum[] = [];
  const unplotted: CampaignImpactMarker[] = [];
  for (const marker of markers) {
    if (plottedDays.has(marker.sent_at)) {
      plotted.push({
        campaignId: marker.campaign_id,
        campaignName: marker.campaign_name,
        sentAt: marker.sent_at,
        label: shortDayLabel(marker.sent_at),
      });
    } else {
      unplotted.push(marker);
    }
  }
  return { plotted, unplotted };
}
